import { useCallback, useEffect, useState } from "react";
import { CLIENT_ID } from "../api/clientId";
import { WebSocketAppointmentDtoActionEnum } from "../api-client";
import useStore from "../helpers/useStore";
import { WebSocketContext } from "../stores/WebSocket/WebSocket.provider";

export interface AbsenceConflict {
  action: WebSocketAppointmentDtoActionEnum;
  loAType?: string;
  start?: string;
  end?: string;
}

/**
 * Therapist ids a broadcast timeslot belongs to. Standalone timeslots (a
 * LeaveOfAbsence among them) carry the user on the item itself, batched
 * payloads carry one per slot.
 */
const affectedUserIds = (data: any): Set<string> => {
  const ids = new Set<string>();
  if (!data) return ids;
  for (const slot of data.timeSlots ?? []) {
    if (slot?.user?.id) ids.add(slot.user.id);
  }
  if (data.user?.id) ids.add(data.user.id);
  return ids;
};

/**
 * Detects concurrent edits of one employee's absences: another user (or another
 * tab) writing a LeaveOfAbsence for `userId` makes the locally rendered calendar
 * stale, so the caller must block further saves and offer a reload.
 *
 * Only the first conflict is kept — once stale, everything after it is noise.
 */
export const useAbsenceConflictWatcher = (userId?: string) => {
  const webSocketStore = useStore(WebSocketContext);
  const [conflict, setConflict] = useState<AbsenceConflict | null>(null);

  useEffect(() => {
    setConflict(null);

    if (!userId) {
      return;
    }

    const handleAppointmentUpdate = (payload: any) => {
      const event = payload?.webSocket ?? payload;
      if (!event?.type || !event?.action) return;

      // Echoes of this tab's own writes are already reflected locally.
      if (event.clientId && event.clientId === CLIENT_ID) return;
      if (event.type !== "TimeSlot") return;

      const data = event.data;
      if (data?.type !== "LeaveOfAbsence") return;
      if (!affectedUserIds(data).has(userId)) return;

      setConflict((current) =>
        current ?? {
          action: event.action,
          loAType: data.loAType ?? data.title,
          start: data.start,
          end: data.end,
        }
      );
    };

    webSocketStore.on("appointmentUpdate", handleAppointmentUpdate);
    return () => webSocketStore.off("appointmentUpdate", handleAppointmentUpdate);
  }, [webSocketStore, userId]);

  const clearConflict = useCallback(() => setConflict(null), []);

  return { conflict, hasConflict: conflict !== null, clearConflict };
};
